import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { 
  Globe, 
  Plus, 
  Trash2, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Server,
  Eye,
  Clock,
  Link
} from 'lucide-react';
import { toast } from 'sonner';

const SubdomainManager = () => {
  const [subdomains, setSubdomains] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [newSubdomain, setNewSubdomain] = useState({
    subdomain: '',
    departmentId: ''
  });

  const { API } = useContext(AuthContext);

  useEffect(() => {
    fetchSubdomains();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchSubdomains, 60000);

    return () => clearInterval(interval);
  }, []);

  const fetchSubdomains = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/master/subdomains`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubdomains(data.subdomains || []);
      }
    } catch (error) {
      console.error('Error fetching subdomains:', error);
      toast.error('Failed to fetch subdomains');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const deptResponse = await fetch(`${API}/master/departments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (deptResponse.ok) {
        const deptData = await deptResponse.json();
        setDepartments(deptData.departments || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast.error('Failed to fetch departments');
    }
  };

  const validateSubdomain = async (subdomain) => {
    if (!subdomain.trim()) {
      setValidationResult(null);
      return;
    }

    setValidating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/master/validate-subdomain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subdomain: subdomain.trim().toLowerCase() })
      });

      const data = await response.json();
      
      if (response.ok) {
        setValidationResult({
          valid: data.available,
          message: data.available ? 'Subdomain is available!' : data.reason || 'Subdomain is not available'
        });
      } else {
        setValidationResult({
          valid: false,
          message: data.detail || 'Failed to validate subdomain'
        });
      }
    } catch (error) {
      console.error('Error validating subdomain:', error);
      setValidationResult({
        valid: false,
        message: 'Error validating subdomain'
      });
    } finally {
      setValidating(false);
    }
  };

  const createSubdomain = async () => {
    if (!newSubdomain.subdomain || !newSubdomain.departmentId) {
      toast.error('Please fill in all required fields');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/master/create-subdomain`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subdomain: newSubdomain.subdomain.trim().toLowerCase(),
          department_id: newSubdomain.departmentId
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Subdomain '${newSubdomain.subdomain}' created successfully!`);
        setNewSubdomain({ subdomain: '', departmentId: '' });
        setValidationResult(null);
        fetchSubdomains(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to create subdomain');
      }
    } catch (error) {
      console.error('Error creating subdomain:', error);
      toast.error('Error creating subdomain');
    } finally {
      setCreating(false);
    }
  };

  const deleteSubdomain = async (subdomain) => {
    if (!confirm(`Are you sure you want to delete the subdomain '${subdomain}'? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/master/delete-subdomain`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subdomain })
      });

      if (response.ok) {
        toast.success(`Subdomain '${subdomain}' deleted successfully!`);
        fetchSubdomains(); // Refresh the list
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to delete subdomain');
      }
    } catch (error) {
      console.error('Error deleting subdomain:', error);
      toast.error('Error deleting subdomain');
    }
  };

  const checkSubdomainStatus = async (subdomain) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API}/master/subdomain-status/${subdomain}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Status: ${data.status} - ${data.message}`);
      } else {
        toast.error('Failed to check subdomain status');
      }
    } catch (error) {
      console.error('Error checking subdomain status:', error);
      toast.error('Error checking subdomain status');
    }
  };

  const handleSubdomainChange = (value) => {
    setNewSubdomain(prev => ({ ...prev, subdomain: value }));
    validateSubdomain(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Loading subdomains...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-slate-800">Subdomain Management</h3>
          <p className="text-slate-600">Manage DNS subdomains for departments</p>
        </div>
        <Button onClick={fetchSubdomains} variant="outline" size="sm">
          <Server className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Create New Subdomain */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Create New Subdomain
          </CardTitle>
          <CardDescription>
            Add a new subdomain for a department
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="subdomain">Subdomain</Label>
              <div className="space-y-2">
                <Input
                  id="subdomain"
                  placeholder="e.g., westpd"
                  value={newSubdomain.subdomain}
                  onChange={(e) => handleSubdomainChange(e.target.value)}
                  className="lowercase"
                />
                {validating && (
                  <div className="flex items-center text-sm text-slate-500">
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    Validating...
                  </div>
                )}
                {validationResult && (
                  <div className={`flex items-center text-sm ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                    {validationResult.valid ? (
                      <CheckCircle className="w-3 h-3 mr-1" />
                    ) : (
                      <AlertCircle className="w-3 h-3 mr-1" />
                    )}
                    {validationResult.message}
                  </div>
                )}
              </div>
              <p className="text-xs text-slate-500">
                Will create: {newSubdomain.subdomain ? `${newSubdomain.subdomain}.reqtrak.com` : 'subdomain.reqtrak.com'}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Select 
                value={newSubdomain.departmentId} 
                onValueChange={(value) => setNewSubdomain(prev => ({ ...prev, departmentId: value }))}
                onOpenChange={(open) => {
                  if (open && departments.length === 0) {
                    fetchDepartments();
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button 
            onClick={createSubdomain} 
            disabled={creating || !validationResult?.valid || !newSubdomain.departmentId}
            className="w-full md:w-auto"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Subdomain
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Existing Subdomains */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Existing Subdomains ({subdomains.length})
          </CardTitle>
          <CardDescription>
            Manage existing department subdomains
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subdomains.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Globe className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No subdomains found.</p>
              <p className="text-sm">Create your first subdomain above.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {subdomains.map((subdomain, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Link className="w-4 h-4 text-blue-500" />
                        <span className="font-medium">{subdomain.subdomain}.reqtrak.com</span>
                        <Badge variant={subdomain.status === 'active' ? 'default' : 'secondary'}>
                          {subdomain.status || 'unknown'}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        Department: {subdomain.department_name || 'Unknown'}
                      </p>
                      {subdomain.created_at && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Created: {new Date(subdomain.created_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => checkSubdomainStatus(subdomain.subdomain)}
                        variant="outline"
                        size="sm"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => deleteSubdomain(subdomain.subdomain)}
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubdomainManager;